// src/components/TransportMap/TransportMap.js
import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, Pane } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './TransportMap.css';
import { useTheme } from '../../ThemeSwitcher';
import { fetchTransportModes, fetchFeatures, fetchYears, fetchFilteredData } from '../../services/data_service';

const TransportMap = ({ data }) => {
  const { isDarkMode } = useTheme();
  const [mapData, setMapData] = useState(null);
  const [transportModes, setTransportModes] = useState([]);
  const [selectedTransportMode, setSelectedTransportMode] = useState('');
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState('');
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingMap, setLoadingMap] = useState(true);

  useEffect(() => {
    const loadTransportModes = async () => {
      try {
        const modes = await fetchTransportModes();
        setTransportModes(modes);
        if (modes.length > 0) setSelectedTransportMode(modes[0]);
      } catch (err) {
        console.error('Failed to load transport modes:', err);
      }
    };
    loadTransportModes();
  }, []);

  useEffect(() => {
    if (!selectedTransportMode) return;
    const loadFeatures = async () => {
      try {
        const features = await fetchFeatures(selectedTransportMode);
        setFeatures(features);
        if (features.length > 0) setSelectedFeature(features[0]);
      } catch (err) {
        console.error('Failed to load features:', err);
      }
    };
    loadFeatures();
  }, [selectedTransportMode]);

  useEffect(() => {
    if (!selectedFeature) return;
    const loadYears = async () => {
      try {
        const years = await fetchYears(selectedTransportMode, selectedFeature);
        setYears(years);
        if (years.length > 0) setSelectedYear(years[years.length - 1]);
      } catch (err) {
        console.error('Failed to load years:', err);
      }
    };
    loadYears();
  }, [selectedFeature]);

  useEffect(() => {
    if (selectedTransportMode && selectedFeature && selectedYear) {
      setLoadingFilters(false);
    }
  }, [selectedTransportMode, selectedFeature, selectedYear]);

  useEffect(() => {
    if (!selectedTransportMode || !selectedFeature || !selectedYear) return;

    setLoadingMap(true);
    setMapData(null);
    const loadFilteredData = async () => {
      try {
        const data = await fetchFilteredData(selectedTransportMode, selectedFeature, selectedYear);
        setMapData(data);
      } catch (err) {
        console.error('Failed to load filtered data:', err);
      } finally {
        setLoadingMap(false);
      }
    };
    loadFilteredData();
  }, [selectedYear]);

  const getStyleForFeature = useCallback((feature) => {
    const props = feature.properties;

    if (selectedFeature === 'AADT') {
      const aadt = props.aadt;
      let color = '#636e72';
      if (aadt === 'No data') color = '#CACFD1';
      else if (aadt >= 1 && aadt <= 1000) color = '#EF951B';
      else if (aadt <= 2500) color = '#E6467C';
      else if (aadt <= 5000) color = '#92BF2C';
      else if (aadt <= 7500) color = '#9A7B4D';
      else if (aadt <= 10000) color = '#6CCABC';
      else color = '#9657EB';

      return {
        color: color,
        weight: 5,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round'
      };
    } else if (selectedFeature === 'Condition') {
      const condition = props.concession;
      const colors = {
        'Potential PPP Corridors': '#F44336',
        'PPP Corridors Ongoing/Committed': '#EF951B',
        'Feasible RoadSIP III PPP Corridors': '#9657EB'
      };

      return {
        color: colors[condition] || '#636e72',
        weight: 5,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round'
      };
    }

    return {
      color: '#636e72',
      weight: 5,
      opacity: 1
    };
  }, [selectedFeature]);

  const getTooltipContent = useCallback((feature) => {
    const props = feature.properties;

    if (selectedFeature === 'AADT') {
      return `
        <div class="road-tooltip">
          <strong>${props.road_name || 'Unnamed Road'}</strong>
          <strong>Traffic (AADT): ${Math.round(props.aadt).toLocaleString()}</strong>
          Type: ${props.road_type || 'Unknown'}
        </div>
      `;
    } else if (selectedFeature === 'Condition') {
      return `
        <div class="road-tooltip">
          <strong>${props.road_name || 'Unnamed Road'}</strong>
          <strong>Condition: ${props.concession || 'Unknown'}</strong>
          District: ${props.district || 'Unknown'}<br />
          Road ID: ${props.road_id || 'Unknown'}
        </div>
      `;
    }

    return `
      <div class="road-tooltip">
        <strong>${props.road_name || 'Unnamed Road'}</strong>
      </div>
    `;
  }, [selectedFeature]);

  const mapStyles = {
    carto: {
      light: {
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      },
      dark: {
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      }
    }
  };

  return (
      <div className="traffic-map">
        <div className="legend-wrapper">
          <div className={`legend ${isDarkMode ? 'dark' : 'light'}`}>
            <h3>Filters</h3>

            {loadingFilters && (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading Filters...</p>
                </div>
            )}
            {!loadingFilters && (
                <div className="filter-controls">
                  <div className="filter-group">
                    <label>Select Transport Mode</label>
                    <select className="filter-select" value={selectedTransportMode}
                            onChange={(e) => setSelectedTransportMode(e.target.value)}>
                      {transportModes.map(mode => (
                          <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Select Indicator</label>
                    <select className="filter-select" value={selectedFeature}
                            onChange={(e) => setSelectedFeature(e.target.value)}>
                      {features.map(feature => (
                          <option key={feature} value={feature}>{feature}</option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Select Year</label>
                    <select className="filter-select" value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}>
                      {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  {loadingMap && (
                      <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading Map...</p>
                      </div>
                  )}
            </div>
            )}
          </div>

          {selectedFeature === 'AADT' && (
              <div className="legend-sections">
                <div className="traffic-volume">
                  <h4>Traffic Volume (AADT)</h4>
                  {[
                    { range: "No data", color: '#CACFD1' },
                    { range: "1 to 1,000", color: '#EF951B' },
                    { range: "1,001 - 2,500", color: '#E6467C' },
                    { range: "2,501 - 5,000", color: '#92BF2C' },
                    { range: "5,001 - 7,500", color: '#9A7B4D' },
                    { range: "7,501 - 10,000", color: '#6CCABC' },
                    { range: "Above 10,000", color: '#9657EB' }
                  ].map((item, i) => (
                      <div key={i} className="legend-item">
                        <div
                            className="legend-line"
                            style={{
                              backgroundColor: item.color,
                              height: '5px',
                              width: '30px'
                            }}
                        />
                        <span>{item.range}</span>
                      </div>
                  ))}
                </div>
              </div>
          )}

          {selectedFeature === 'Condition' && (
              <div className="legend-sections">
                <div className="condition">
                  <h4>Road Condition</h4>
                  {[
                    { label: "Potential PPP Corridors", color: '#F44336' },
                    { label: "PPP Corridors Ongoing/Committed", color: '#EF951B' },
                    { label: "Feasible RoadSIP III PPP Corridors", color: '#9657EB' }
                  ].map((item, i) => (
                      <div key={i} className="legend-item">
                        <div
                            className="legend-line"
                            style={{
                              backgroundColor: item.color,
                              height: '5px',
                              width: '30px'
                            }}
                        />
                        <span>{item.label}</span>
                      </div>
                  ))}
                </div>
              </div>
          )}
        </div>

        <div className="map-wrapper">
          <MapContainer
              center={[-13.25, 27.8]}
              zoom={7}
              className={`map-container ${isDarkMode ? 'dark' : 'light'}`}
          >
            <TileLayer
                url={isDarkMode ? mapStyles.carto.dark.url : mapStyles.carto.light.url}
                attribution={mapStyles.carto.light.attribution}
            />

            {mapData && (
                <>
                  <Pane name="geometry" style={{ zIndex: 400 }}>
                    <GeoJSON
                        data={{
                          type: "FeatureCollection",
                          features: mapData.map(record => ({
                            type: "Feature",
                            properties: {
                              ...record,
                              road_name: record.road_name || 'Unnamed Road'
                            },
                            geometry: record.transport_geometry
                          }))
                        }}
                        style={getStyleForFeature}
                        onEachFeature={(feature, layer) => {
                          layer.bindTooltip(getTooltipContent(feature), {
                            sticky: true,
                            className: 'custom-tooltip'
                          });
                        }}
                    />
                  </Pane>

                  {selectedFeature === 'AADT' && (
                      <Pane name="toll-plazas" style={{ zIndex: 500 }}>
                        {mapData
                            .filter(d => d.toll_station && d.latitude && d.longitude)
                            .map((plaza, index) => (
                                <CircleMarker
                                    key={index}
                                    center={[plaza.latitude, plaza.longitude]}
                                    radius={7}
                                    pathOptions={{
                                      fillColor: '#0000CC',
                                      color: '#0000CC',
                                      opacity: 1,
                                      weight: 1
                                    }}
                                >
                                  <Tooltip
                                      permanent={false}
                                      direction="top"
                                      offset={[0, -5]}
                                      className="custom-tooltip"
                                  >
                                    <div className="toll-tooltip">
                                      <strong>{plaza.toll_station}</strong>
                                      <div>Traffic (AADT): {Math.round(plaza.aadt).toLocaleString()}</div>
                                    </div>
                                  </Tooltip>
                                </CircleMarker>
                            ))}
                      </Pane>
                  )}
                </>
            )}
          </MapContainer>
        </div>
      </div>
  );
};

export default TransportMap;